import { useCallback, useSyncExternalStore } from "react";

import {
  SETTINGS_CHANGED_EVENT,
  type SettingsChangedDetail,
  type SettingsKey,
} from "../lib/settings";

/**
 * Keep a React component in sync with a Stream setting.
 *
 * Stream settings live in localStorage and are mutated through the typed
 * accessors in `lib/settings.ts`. This hook wraps `useSyncExternalStore`
 * so that:
 *
 *   - Multiple components reading the same key always agree
 *   - Writes made in another tab propagate (native `storage` event)
 *   - Same-tab writes propagate via custom `stream:settings-changed` event
 *
 * Usage:
 *
 * ```ts
 * const visible = useSetting(
 *   SETTINGS_KEYS.personalStatsVisible,
 *   () => settings.personalStats.visible.get(),
 *   true,
 * );
 * ```
 */
export function useSetting<T>(
  key: SettingsKey,
  getSnapshot: () => T,
  serverSnapshot: T,
): T {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === "undefined") return () => {};

      const handleCustomEvent = (event: Event) => {
        const detail = (event as CustomEvent<SettingsChangedDetail>).detail;
        if (!detail || detail.key === key) {
          onChange();
        }
      };

      const handleStorage = (event: StorageEvent) => {
        // `storage` only fires in *other* tabs
        if (event.key === null || event.key === key) {
          onChange();
        }
      };

      window.addEventListener(SETTINGS_CHANGED_EVENT, handleCustomEvent);
      window.addEventListener("storage", handleStorage);
      return () => {
        window.removeEventListener(SETTINGS_CHANGED_EVENT, handleCustomEvent);
        window.removeEventListener("storage", handleStorage);
      };
    },
    [key],
  );

  return useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => serverSnapshot,
  );
}
